'use client';

import { useEffect, useId, useRef } from 'react';
import { cn } from '../../lib/utils';
import { LegacyRainaBody } from './LegacyRainaBody';
import { LegacyRainaEars } from './LegacyRainaEars';
import { LegacyRainaHead } from './LegacyRainaHead';
import { getHexagonClipPath, LegacyRainaHexagonBadge } from './LegacyRainaHexagonBadge';
import { LegacyRainaHoodBottom } from './LegacyRainaHoodBottom';
import { LegacyRainaHorn } from './LegacyRainaHorn';
import { RainaHead } from './RainaHead';
import { DEFAULT_EXPRESSION, type RainaAnimation, type RainaExpression, useRainaAnimation } from './useRainaAnimation';
import { useRainaHover } from './useRainaHover';
import { useRainaInteraction } from './useRainaInteraction';

/**
 * Display variant. There are two families:
 *
 * **Current — `head`** (default): the redesigned MagicSchool character. It is a single,
 * self-contained SVG (`viewBox="0 0 26 28"`) rendered by {@link RainaHead}. It supports only
 * **eye tracking** and **blink** — there are intentionally no expressions, wave, nod, head
 * tilt, or ear twitch. Eye tracking reuses the shared `useEyeTracking` hook (its gaze target is
 * parameterized so the same math works against this head's smaller viewBox); blink is a CSS
 * `scaleY` squash of the eyes driven by `useRainaAnimation`'s `isBlinking`. When fully static
 * (no `eyeTracking` and no `idleAnimations`, e.g. an icon) it holds a fixed pose looking up and
 * to her left; when animated, neutral gaze is centered and the cursor/idle-wander move the
 * pupils symmetrically.
 *
 * **Legacy — `legacy-full` | `legacy-head` | `legacy-badge`**: the previous expressive unicorn,
 * composed from many part components ({@link LegacyRainaHead}, body, ears, horn, hood, badge)
 * and supporting the full set of expressions and animations. `legacy-full` is the whole body,
 * `legacy-head` an avatar crop, `legacy-badge` the head in a hexagonal frame.
 *
 * ### Why both exist
 * HOME-256 introduced the new `head` as the canonical character, but the legacy art is still in
 * use across many surfaces. Rather than a risky big-bang swap, the existing variants were
 * renamed with a `legacy-` prefix (additive, no behavior change) so callers opt in to the new
 * head one surface at a time; the legacy family is removed once nothing references it. The new
 * head is deliberately scoped to eye tracking + blink — the legacy expression/animation system
 * does not carry over — so consumers needing rich animation stay on `legacy-*` for now.
 */
export type RainaVariant = 'head' | 'legacy-full' | 'legacy-head' | 'legacy-badge';

export interface RainaProps {
  /** Additional CSS classes */
  className?: string;
  /** Enable eye tracking (follows cursor). Default: false */
  eyeTracking?: boolean;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Aria label for accessibility */
  'aria-label'?: string;
  /** Current expression to display */
  expression?: RainaExpression;
  /** Animation(s) to trigger */
  animation?: RainaAnimation | RainaAnimation[];
  /** Enable idle animations like periodic blinking. Default: false */
  idleAnimations?: boolean;
  /** Callback when an animation completes */
  onAnimationComplete?: (animation: RainaAnimation) => void;
  /** Enable click interactions (surprised reaction + wave). Default: false */
  interactive?: boolean;
  /** Custom click handler (overrides default reaction) */
  onClick?: () => void;
  /** Display variant: 'head' (current head, default), or legacy 'legacy-full' / 'legacy-head' / 'legacy-badge'. */
  variant?: RainaVariant;
}

export type { RainaExpression, RainaAnimation };

const SIZE_CLASSES = { xs: 'w-9', sm: 'w-24', md: 'w-48', lg: 'w-72' } as const;

/** Head-only variant inner container transform */
const HEAD_ONLY_TRANSFORM = { transform: 'scale(0.88) translate(-1%, -5%)', transformOrigin: 'center center' };

/** Badge variant inner container transform - positions Raina within the hexagon (229x250 viewBox) */
const BADGE_TRANSFORM = { transform: 'scale(0.85) translate(0, -8%)', transformOrigin: 'center center' };

/**
 * MagicSchool's AI mascot character — an expressive unicorn with animations, eye tracking, and interactive click reactions.
 *
 * ### When to use
 * - As a branded visual presence for the AI assistant (e.g., chat empty states, onboarding)
 * - When you want an expressive, animated character to provide personality to a UI
 * - For avatar-style representations of Raina using the `head` or `badge` variant
 *
 * ### When NOT to use
 * - For generic loading indicators: use `Spinner` instead
 * - For static illustrations or icons: use `Icon` or an `<img>` instead
 * - In performance-sensitive list views with many instances — each Raina renders complex SVG with potential animations
 *
 * ### API
 * - `variant`: Display mode — `'head'` (default) for the current compact head (eye tracking + blink); legacy `'legacy-full'` full body, `'legacy-head'` avatar crop, `'legacy-badge'` hexagonal frame
 * - `size`: Dimensions — `'xs'`, `'sm'`, `'md'`, `'lg'`
 * - `expression`: Facial expression (18 options including `'happy'`, `'thinking'`, `'surprised'`, `'listening'`, etc.)
 * - `animation`: Trigger an animation — `'wave'`, `'nod'`, `'blink'`, `'headTilt'`, `'earTwitch'`
 * - `eyeTracking`: When `true`, pupils follow the cursor
 * - `idleAnimations`: When `true`, enables periodic blinks and micro-movements
 * - `interactive`: When `true`, enables click-to-react with region-based responses (ear, head, hand)
 * - `onAnimationComplete`: Callback fired when a triggered animation finishes
 *
 * ### Visual states
 * - **Static** (default): Renders a still SVG with the specified expression — zero animation cost
 * - **Idle**: With `idleAnimations`, periodic blinks and subtle movements. Paused during hover in interactive mode.
 * - **Interactive hover**: Shows `listening` expression when hovered with `interactive` enabled
 * - **Interactive click**: Region-based reactions — ear click triggers surprised + ear twitch, head/body triggers happy + nod, hand triggers happy + wave
 *
 * ### Accessibility
 * - Renders with `role="img"` and a default `aria-label` of "Raina, the MagicSchool AI mascot"
 * - When `interactive` or `onClick` is set, switches to `role="button"` with `tabIndex={0}` and keyboard support (Enter/Space)
 * - Provide a custom `aria-label` when the context requires a more specific description
 */
export function Raina({
  className = '',
  eyeTracking = false,
  size = 'md',
  'aria-label': ariaLabel,
  expression: expressionProp,
  animation,
  idleAnimations = false,
  onAnimationComplete,
  interactive = false,
  onClick,
  variant = 'head',
}: RainaProps) {
  const svgId = useId();
  const isHeadOnly = variant === 'head';
  const legacyIsHeadOnly = variant === 'legacy-head';
  const isBadge = variant === 'legacy-badge';
  const containerRef = useRef<HTMLDivElement>(null);

  const { isHovering, onMouseEnter, onMouseMove, onMouseLeave } = useRainaHover({
    interactive,
    containerRef,
  });

  const baseExpression: RainaExpression = expressionProp ? expressionProp : isHovering && interactive ? 'listening' : DEFAULT_EXPRESSION;

  // Pause idle animations during hover in interactive mode
  const effectiveIdleAnimations = idleAnimations && !(interactive && isHovering);

  const {
    isWaving,
    isBlinking,
    isNodding,
    isEarTwitching,
    isHeadTilting,
    headTiltDirection,
    triggerWave,
    triggerNod,
    triggerEarTwitch,
    triggerHeadTilt,
    handleAnimationEnd,
  } = useRainaAnimation({
    expression: baseExpression,
    animation,
    idleAnimations: effectiveIdleAnimations,
    onAnimationComplete,
  });

  const isAnimating = isWaving || isBlinking || isNodding || isEarTwitching || isHeadTilting;

  const { interactionExpression, pendingAnimation, handleClick, handleKeyDown } = useRainaInteraction({
    interactive,
    onClick,
    isAnimating,
    isHeadOnly: isHeadOnly || legacyIsHeadOnly,
  });

  // Trigger animations requested by interactions
  useEffect(() => {
    if (!pendingAnimation) return;

    switch (pendingAnimation) {
      case 'nod':
        triggerNod();
        break;
      case 'wave':
        triggerWave();
        break;
      case 'earTwitch':
        triggerEarTwitch();
        break;
      case 'headTilt':
        triggerHeadTilt();
        break;
    }
  }, [pendingAnimation, triggerNod, triggerWave, triggerEarTwitch, triggerHeadTilt]);

  // Interaction expression takes priority over base expression
  const currentExpression: RainaExpression = expressionProp ?? interactionExpression ?? baseExpression;

  const effectiveIsWaving = legacyIsHeadOnly || isBadge ? false : isWaving;
  const isClickable = interactive || onClick;

  return (
    <div
      data-spellbook-name="raina"
      ref={containerRef}
      className={cn(
        'relative',
        SIZE_CLASSES[size],
        isClickable && 'select-none [&_[data-region]]:cursor-pointer focus-visible:outline-none focus-visible-ring rounded-xl',
        className,
      )}
      style={{
        aspectRatio: isHeadOnly || legacyIsHeadOnly ? '1 / 1' : isBadge ? '229 / 250' : '428 / 654',
        ...(legacyIsHeadOnly ? { clipPath: 'inset(-15% -15% -15% -15%)' } : {}),
      }}
      role={isClickable ? 'button' : 'img'}
      aria-label={ariaLabel || 'Raina, the MagicSchool AI mascot'}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      tabIndex={isClickable ? 0 : undefined}
    >
      {isBadge ? (
        <>
          {/* Hexagon background */}
          <LegacyRainaHexagonBadge svgId={svgId} />
          {/* Ears rendered outside clip path so they extend beyond hexagon */}
          <div className="pointer-events-none absolute inset-0 overflow-visible" style={{ ...BADGE_TRANSFORM }}>
            <div className="absolute" style={{ width: '100%', aspectRatio: '428 / 654' }}>
              <LegacyRainaEars
                svgId={svgId}
                isEarTwitching={isEarTwitching}
                isNodding={isNodding}
                isHeadTilting={isHeadTilting}
                headTiltDirection={headTiltDirection}
                onEarTwitchEnd={() => handleAnimationEnd('earTwitch')}
              />
            </div>
          </div>
          {/* Clipped head content (body clipped at hexagon bottom) */}
          <div className="absolute inset-0 overflow-visible" style={{ clipPath: getHexagonClipPath() }}>
            <div className="absolute" style={{ width: '100%', aspectRatio: '428 / 654', ...BADGE_TRANSFORM }}>
              <LegacyRainaBody
                svgId={svgId}
                isWaving={effectiveIsWaving}
                idleAnimations={idleAnimations}
                onWaveEnd={() => handleAnimationEnd('wave')}
              />
              <LegacyRainaHoodBottom svgId={svgId} idleAnimations={idleAnimations} />
              <LegacyRainaHead
                svgId={svgId}
                eyeTracking={eyeTracking}
                expression={currentExpression}
                isBlinking={isBlinking}
                isNodding={isNodding}
                isHeadTilting={isHeadTilting}
                headTiltDirection={headTiltDirection}
                isEarTwitching={isEarTwitching}
                idleAnimations={effectiveIdleAnimations}
                renderEars={false}
                renderHorn={false}
                onNodEnd={() => handleAnimationEnd('nod')}
                onHeadTiltEnd={() => handleAnimationEnd('headTilt')}
                onEarTwitchEnd={() => handleAnimationEnd('earTwitch')}
                onBlinkEnd={() => handleAnimationEnd('blink')}
              />
            </div>
          </div>
          {/* Horn rendered outside clip path so it extends beyond hexagon */}
          <div className="pointer-events-none absolute inset-0 overflow-visible" style={{ ...BADGE_TRANSFORM }}>
            <div className="absolute" style={{ width: '100%', aspectRatio: '428 / 654' }}>
              <LegacyRainaHorn svgId={svgId} isNodding={isNodding} isHeadTilting={isHeadTilting} headTiltDirection={headTiltDirection} />
            </div>
          </div>
        </>
      ) : isHeadOnly ? (
        <RainaHead
          svgId={svgId}
          eyeTracking={eyeTracking}
          isBlinking={isBlinking}
          idleAnimations={effectiveIdleAnimations}
          onBlinkEnd={() => handleAnimationEnd('blink')}
        />
      ) : legacyIsHeadOnly ? (
        <div className="absolute" style={{ width: '100%', aspectRatio: '428 / 654', ...HEAD_ONLY_TRANSFORM }}>
          <LegacyRainaHead
            svgId={svgId}
            eyeTracking={eyeTracking}
            expression={currentExpression}
            isBlinking={isBlinking}
            isNodding={isNodding}
            isHeadTilting={isHeadTilting}
            headTiltDirection={headTiltDirection}
            isEarTwitching={isEarTwitching}
            idleAnimations={effectiveIdleAnimations}
            onNodEnd={() => handleAnimationEnd('nod')}
            onHeadTiltEnd={() => handleAnimationEnd('headTilt')}
            onEarTwitchEnd={() => handleAnimationEnd('earTwitch')}
            onBlinkEnd={() => handleAnimationEnd('blink')}
          />
        </div>
      ) : (
        <>
          <LegacyRainaBody
            svgId={svgId}
            isWaving={effectiveIsWaving}
            idleAnimations={idleAnimations}
            onWaveEnd={() => handleAnimationEnd('wave')}
          />
          <LegacyRainaHoodBottom svgId={svgId} idleAnimations={idleAnimations} />
          <LegacyRainaHead
            svgId={svgId}
            eyeTracking={eyeTracking}
            expression={currentExpression}
            isBlinking={isBlinking}
            isNodding={isNodding}
            isHeadTilting={isHeadTilting}
            headTiltDirection={headTiltDirection}
            isEarTwitching={isEarTwitching}
            idleAnimations={effectiveIdleAnimations}
            onNodEnd={() => handleAnimationEnd('nod')}
            onHeadTiltEnd={() => handleAnimationEnd('headTilt')}
            onEarTwitchEnd={() => handleAnimationEnd('earTwitch')}
            onBlinkEnd={() => handleAnimationEnd('blink')}
          />
        </>
      )}
    </div>
  );
}
